import { FunctionComponent } from 'react';
import { Button, Empty, Select, Table, Tag, Tabs, Tooltip, Typography } from 'antd';
import {
    CalendarOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    FileExcelOutlined,
    FileTextOutlined,
    PlusCircleOutlined,
    PlusOutlined,
    ReadOutlined,
    SaveOutlined,
    TeamOutlined,
    TrophyOutlined,
} from '@ant-design/icons';
import { MaClasseViewProps } from './types';
import { SwitchFormItem } from '../../../components/common/SwitchFormItem';
import { SelectFormItem } from '../../../components/common/SelectFormItem';
import { getResultatOptions } from '../../../components/common/commoninputs';
import { ColumnsType } from 'antd/es/table';
import { EleveEnrichedDto } from '../../../services/eleve';
import { FeuillePresenceDtoF, BulletinDtoF } from '../../../services/classe';
import dayjs from 'dayjs';
import { APPLICATION_DATE_FORMAT, firstLettertoUpperCase } from '../../../utils/FormUtils';

export const MaClasseDesktopView: FunctionComponent<MaClasseViewProps> = ({
    classe,
    elevesEnriched,
    feuillesPresence,
    bulletins,
    selectedEleveId,
    vueDetaille,
    isAdmin,
    onCreateFeuillePresence,
    onViewFeuille,
    onDeleteFeuille,
    onVueDetailleSwitch,
    exportData,
    onModifierResultat,
    onEnregistrerResultat,
    loadBulletinsEleve,
    onCreerBulletin,
    onModifierBulletin,
    onDeleteBulletin,
    getBulletinPdfButton,
    getTauxReussite,
    getJourClasse,
}) => {

    // ─── Colonnes table effectif simple ───────────────────────────────────────
    const columnsEffectifSimple: ColumnsType<EleveEnrichedDto> = [
        {
            title: 'Nom',
            key: 'nom',
            dataIndex: 'nom',
            sorter: (a, b) => a.nom.localeCompare(b.nom),
        },
        {
            title: 'Prénom',
            key: 'prenom',
            dataIndex: 'prenom',
        },
        {
            title: 'Date naissance',
            key: 'dateNaissance',
            dataIndex: 'dateNaissance',
        },
        {
            title: 'Niveau',
            key: 'niveauInterne',
            dataIndex: 'niveauInterne',
            render: (val) => <Tag color="geekblue">{val}</Tag>,
        },
    ];

    // ─── Colonnes table effectif détaillé ─────────────────────────────────────
    const columnsEffectifDetaille: ColumnsType<EleveEnrichedDto> = [
        ...columnsEffectifSimple,
        {
            title: 'Responsable légal',
            key: 'responsable',
            render: (_, record) => `${record.prenomResponsableLegal} ${record.nomResponsableLegal}`,
        },
        {
            title: 'Mobile',
            key: 'mobile',
            dataIndex: 'mobile',
        },
        {
            title: 'Contact urgence',
            key: 'contactUrgence',
            render: (_, record) => `${record.prenomContactUrgence} ${record.nomContactUrgence}`,
        },
        {
            title: 'Mobile urgence',
            key: 'mobileContactUrgence',
            dataIndex: 'mobileContactUrgence',
        },
        {
            title: 'Photos / Vidéos',
            key: 'autorisationMedia',
            dataIndex: 'autorisationMedia',
            render: (val) => val ? <Tag color="green">Oui</Tag> : <Tag color="red">Non</Tag>,
        },
        {
            title: 'Rentre seul',
            key: 'autorisationAutonomie',
            dataIndex: 'autorisationAutonomie',
            render: (val) => val ? <Tag color="green">Oui</Tag> : <Tag color="red">Non</Tag>,
        },
    ];

    // ─── Colonnes feuilles de présence ────────────────────────────────────────
    const getListeAbsentsTooltip = (presenceEleves: FeuillePresenceDtoF['presenceEleves']) => {
        const absents = presenceEleves.filter((p) => !p.present);
        return absents.map((p) =>
            classe?.liensClasseEleves
                ?.filter((l) => l.eleve.id === p.idEleve)
                .map((l, i) => (
                    <span key={i}>
                        {l.eleve.prenom} {l.eleve.nom}
                        <br />
                    </span>
                ))
        );
    };

    const columnsPresences: ColumnsType<FeuillePresenceDtoF> = [
        {
            title: 'Date',
            key: 'date',
            render: (_, record) =>
                dayjs(record.date, APPLICATION_DATE_FORMAT).format(APPLICATION_DATE_FORMAT),
            sorter: (a, b) =>
                dayjs(a.date, APPLICATION_DATE_FORMAT).valueOf() -
                dayjs(b.date, APPLICATION_DATE_FORMAT).valueOf(),
            defaultSortOrder: 'descend',
        },
        {
            title: 'Absents',
            key: 'absents',
            render: (_, record) => {
                const absents = record.presenceEleves.filter((p) => !p.present);
                return absents.length > 0 ? (
                    <Tooltip title={getListeAbsentsTooltip(record.presenceEleves)} color="geekblue">
                        <Tag color="red" style={{ cursor: 'help' }}>{absents.length}</Tag>
                    </Tooltip>
                ) : (
                    <Tag color="red">0</Tag>
                );
            },
        },
        {
            title: 'Présents',
            key: 'presents',
            render: (_, record) => {
                const presents = record.presenceEleves.filter((p) => p.present);
                return <Tag color="green">{presents.length}</Tag>;
            },
        },
        {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Tooltip title="Voir la feuille" color="geekblue">
                        <Button
                            type="default"
                            icon={<EyeOutlined />}
                            onClick={() => onViewFeuille(record, true)}
                        />
                    </Tooltip>
                    <Tooltip title="Modifier" color="geekblue">
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => onViewFeuille(record, false)}
                        />
                    </Tooltip>
                    <Button
                        type="primary"
                        icon={<DeleteOutlined />}
                        onClick={() => onDeleteFeuille(record)}
                        danger
                    />
                </div>
            ),
        },
    ];

    // ─── Colonnes bulletins ────────────────────────────────────────────────────
    const columnsBulletins: ColumnsType<BulletinDtoF> = [
        {
            title: 'Mois',
            key: 'mois',
            render: (_, record) =>
                firstLettertoUpperCase(dayjs().month(record.mois! - 1).format('MMMM')),
        },
        {
            title: 'Année',
            key: 'annee',
            dataIndex: 'annee',
        },
        {
            title: 'Absences',
            key: 'nbAbsences',
            dataIndex: 'nbAbsences',
            render: (val) => <Tag color="orange">{val}</Tag>,
        },
        {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, record) => {
                const isLocked = record.complet === true && !isAdmin;
                const tooltipTitle = isLocked ? 'Bulletin complet, modification réservée aux administrateurs' : '';
                return (
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <Tooltip title={tooltipTitle}>
                            <Button
                                type="primary"
                                icon={<EditOutlined />}
                                onClick={() => onModifierBulletin(record.id!)}
                                disabled={isLocked}
                            />
                        </Tooltip>
                        <Tooltip title={tooltipTitle}>
                            <Button
                                type="primary"
                                icon={<DeleteOutlined />}
                                onClick={() => onDeleteBulletin(record.id!)}
                                danger
                                disabled={isLocked}
                            />
                        </Tooltip>
                        <Tooltip color="geekblue" title="Générer le bulletin au format PDF">
                            {getBulletinPdfButton(record.id!)}
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    // ─── Colonnes résultats ────────────────────────────────────────────────────
    const columnsResultats: ColumnsType<EleveEnrichedDto> = [
        {
            title: 'Nom',
            key: 'nom',
            dataIndex: 'nom',
            sorter: (a, b) => a.nom.localeCompare(b.nom),
        },
        {
            title: 'Prénom',
            key: 'prenom',
            dataIndex: 'prenom',
        },
        {
            title: 'Niveau',
            key: 'niveauInterne',
            dataIndex: 'niveauInterne',
            render: (val) => <Tag color="geekblue">{val}</Tag>,
        },
        {
            title: 'Résultat',
            key: 'resultat',
            render: (_, record) => (
                <Select
                    style={{ width: '100%', minWidth: 160 }}
                    value={record.resultat}
                    options={getResultatOptions()}
                    onChange={(value) => onModifierResultat(record.id, value)}
                />
            ),
        },
    ];

    function formatTotal(total: number) {
        return <Tag color="geekblue">Total : <strong>{total}</strong></Tag>;
    }

    // ─── Hero Section ──────────────────────────────────────────────────────────
    const renderHero = () => (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginTop: '16px',
            paddingBottom: '16px',
            borderBottom: '3px solid #4FC3F7',
            marginBottom: '24px',
            flexWrap: 'wrap',
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                borderRadius: '10px',
                backgroundColor: '#4FC3F7',
                flexShrink: 0,
            }}>
                <ReadOutlined style={{ fontSize: '24px', color: '#ffffff' }} />
            </div>
            <div style={{ flex: 1 }}>
                <Typography.Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
                    {classe?.libelle ?? 'Ma Classe'}
                </Typography.Title>
                <Typography.Text type="secondary">
                    Année scolaire {classe?.debutAnneeScolaire} / {classe?.finAnneeScolaire}
                </Typography.Text>
            </div>
            {/* Stats inline */}
            <div className="mes-classes-stats-bar" style={{ margin: 0, borderBottom: 'none', background: 'transparent', padding: '0 8px' }}>
                <div className="mes-classes-stat-item">
                    <TeamOutlined className="mes-classes-stat-icon" />
                    <div>
                        <div className="mes-classes-stat-value">{elevesEnriched.length}</div>
                        <div className="mes-classes-stat-label">Élèves</div>
                    </div>
                </div>
                {getJourClasse() && (
                    <>
                        <div className="mes-classes-stat-divider" />
                        <div className="mes-classes-stat-item">
                            <CalendarOutlined className="mes-classes-stat-icon" />
                            <div>
                                <div className="mes-classes-stat-value" style={{ fontSize: '14px' }}>{getJourClasse()}</div>
                                <div className="mes-classes-stat-label">Jour de classe</div>
                            </div>
                        </div>
                    </>
                )}
                <div className="mes-classes-stat-divider" />
                <div className="mes-classes-stat-item">
                    <TrophyOutlined className="mes-classes-stat-icon" />
                    <div>
                        <div className="mes-classes-stat-value" style={{ fontSize: '14px' }}>
                            <>{getTauxReussite()}</>
                        </div>
                        <div className="mes-classes-stat-label">Taux de réussite</div>
                    </div>
                </div>
            </div>
        </div>
    );

    // ─── Tab Effectif ──────────────────────────────────────────────────────────
    const renderTabEffectif = () => (
        <div className="ma-classe-tab-content">
            <div className="ma-classe-tab-toolbar">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <SwitchFormItem
                        name="vueDetaille"
                        value={vueDetaille}
                        onChange={onVueDetailleSwitch}
                        label="Vue détaillée"
                    />
                </div>
                <Tooltip title="Exporter la liste des élèves au format Excel" color="geekblue">
                    <Button
                        type="primary"
                        icon={<FileExcelOutlined />}
                        onClick={exportData}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    >
                        Exporter
                    </Button>
                </Tooltip>
            </div>
            <Table
                dataSource={elevesEnriched}
                columns={vueDetaille ? columnsEffectifDetaille : columnsEffectifSimple}
                pagination={{ pageSize: 10, showTotal: formatTotal }}
                rowKey={(record) => record.id}
                scroll={{ x: vueDetaille ? 1200 : undefined }}
                size="small"
            />
        </div>
    );

    // ─── Tab Présences ─────────────────────────────────────────────────────────
    const renderTabPresences = () => (
        <div className="ma-classe-tab-content">
            <div className="ma-classe-tab-toolbar">
                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: '13px' }}>
                    {feuillesPresence.length} feuille{feuillesPresence.length !== 1 ? 's' : ''} enregistrée{feuillesPresence.length !== 1 ? 's' : ''}
                </span>
                <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    onClick={onCreateFeuillePresence}
                >
                    Nouvelle feuille
                </Button>
            </div>
            {feuillesPresence.length === 0 ? (
                <Empty
                    description="Aucune feuille de présence pour cette classe"
                    style={{ padding: '48px 0' }}
                >
                    <Button type="primary" icon={<PlusCircleOutlined />} onClick={onCreateFeuillePresence}>
                        Créer la première feuille
                    </Button>
                </Empty>
            ) : (
                <Table
                    dataSource={feuillesPresence}
                    columns={columnsPresences}
                    pagination={{ pageSize: 10, showTotal: formatTotal }}
                    rowKey={(record) => String(record.date?.valueOf?.() ?? record.id)}
                    size="small"
                />
            )}
        </div>
    );

    // ─── Tab Bulletins ─────────────────────────────────────────────────────────
    const renderTabBulletins = () => (
        <div className="ma-classe-tab-content">
            <div className="ma-classe-tab-toolbar">
                <div style={{ flex: 1, maxWidth: '400px' }}>
                    <SelectFormItem
                        name="eleveid"
                        label="Élève"
                        options={elevesEnriched.map((eleve) => ({
                            value: eleve.id,
                            label: `${eleve.prenom} ${eleve.nom}`,
                        }))}
                        onChange={loadBulletinsEleve}
                    />
                </div>
                <Tooltip
                    title={!selectedEleveId ? 'Sélectionnez un élève pour créer un bulletin' : 'Créer un nouveau bulletin'}
                    color="geekblue"
                >
                    <Button
                        icon={<PlusOutlined />}
                        type="primary"
                        disabled={!selectedEleveId}
                        onClick={onCreerBulletin}
                    >
                        Créer un bulletin
                    </Button>
                </Tooltip>
            </div>
            {!selectedEleveId ? (
                <Empty
                    description="Sélectionnez un élève pour afficher ses bulletins"
                    style={{ padding: '48px 0' }}
                />
            ) : bulletins.length === 0 ? (
                <Empty
                    description="Aucun bulletin pour cet élève"
                    style={{ padding: '48px 0' }}
                >
                    <Button type="primary" icon={<PlusOutlined />} onClick={onCreerBulletin}>
                        Créer le premier bulletin
                    </Button>
                </Empty>
            ) : (
                <Table
                    dataSource={bulletins}
                    columns={columnsBulletins}
                    pagination={{ pageSize: 10, showTotal: formatTotal }}
                    rowKey={(record) => `${record.mois}-${record.annee}`}
                    size="small"
                />
            )}
        </div>
    );

    // ─── Tab Résultats ─────────────────────────────────────────────────────────
    const renderTabResultats = () => (
        <div className="ma-classe-tab-content">
            <Table
                dataSource={elevesEnriched}
                columns={columnsResultats}
                pagination={{ pageSize: 10, showTotal: formatTotal }}
                rowKey={(record) => record.id}
                size="small"
                style={{ marginBottom: '16px' }}
            />
            <div className="ma-classe-resultats-footer">
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={onEnregistrerResultat}
                    size="large"
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                >
                    Enregistrer les résultats
                </Button>
                <div className="ma-classe-taux-info">
                    <TrophyOutlined style={{ color: '#4FC3F7', marginRight: '6px' }} />
                    <span>Taux de réussite : </span>
                    <strong style={{ marginLeft: '4px' }}><>{getTauxReussite()}</></strong>
                </div>
            </div>
        </div>
    );

    // ─── Tabs items ────────────────────────────────────────────────────────────
    const tabItems = [
        {
            key: 'effectif',
            label: (
                <span className="ma-classe-tab-label">
                    <TeamOutlined />
                    Effectif
                    <Tag color="geekblue" style={{ marginLeft: '6px', fontSize: '11px' }}>
                        {elevesEnriched.length}
                    </Tag>
                </span>
            ),
            children: renderTabEffectif(),
        },
        {
            key: 'presences',
            label: (
                <span className="ma-classe-tab-label">
                    <CalendarOutlined />
                    Présences
                    {feuillesPresence.length > 0 && (
                        <Tag color="blue" style={{ marginLeft: '6px', fontSize: '11px' }}>
                            {feuillesPresence.length}
                        </Tag>
                    )}
                </span>
            ),
            children: renderTabPresences(),
        },
        {
            key: 'bulletins',
            label: (
                <span className="ma-classe-tab-label">
                    <FileTextOutlined />
                    Bulletins
                </span>
            ),
            children: renderTabBulletins(),
        },
        {
            key: 'resultats',
            label: (
                <span className="ma-classe-tab-label">
                    <TrophyOutlined />
                    Résultats
                </span>
            ),
            children: renderTabResultats(),
        },
    ];

    return (
        <div className="ma-classe-desktop-wrapper">
            {renderHero()}
            <Tabs
                type="card"
                items={tabItems}
                defaultActiveKey="effectif"
                className="ma-classe-tabs"
            />
        </div>
    );
};
